// ===============================================================
// ProBank — International Transfer (Elite only)
//
// Cross-border transfers with TRANSACTION PIN authorization.
//
// ORDER OF CHECKS (like real banks):
//   1. Insufficient balance → ALWAYS shown first, before anything
//   2. Delivery gate (no subscription + no card) → warning, only
//      when the user can actually afford the transfer
//   3. PIN → transfer
// ===============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCountry } from "../context/CountryContext";
import PinPad from "../components/PinPad";
import { fetchDeliveryGate, DeliveryGateModal } from "../components/DeliveryGate";

const API = process.env.REACT_APP_API_URL;

export default function InternationalTransferPage() {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();
  const cur = config?.currencySymbol || (isGhana ? "GH₵" : "₦");

  const theme = {
    bg: isGhana ? "#171204" : "#050c14",
    card: isGhana ? "#221b07" : "#0a1626",
    line: isGhana ? "#3d3413" : "#1a2f47",
    accent: isGhana ? "#facc15" : "#38bdf8",
    accentSoft: isGhana ? "#facc1514" : "#38bdf814",
    text: "#f4f6f8",
    sub: isGhana ? "#a89b6b" : "#7a93ab",
    inputBg: isGhana ? "#171204" : "#07101e",
    accentText: isGhana ? "#171204" : "#04121f",
  };

  const [user, setUser] = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [countryName, setCountryName] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [swiftCode, setSwiftCode] = useState("");
  const [amount, setAmount] = useState("");
  const [recents, setRecents] = useState([]);
  const [stage, setStage] = useState("form"); // form | confirm | processing | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [blocker, setBlocker] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);

  // Delivery gate — checked on mount, enforced before the PIN pad
  const [canDeliver, setCanDeliver] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      setUser(u);

      // Client-side guard — Elite only. The server enforces it too.
      const isElite = (u.plan || "").toLowerCase() === "elite" && u.subscriptionActive === true;
      if (!isElite) {
        navigate("/transfer-select", { replace: true });
      }
    } catch {
      navigate("/transfer-select", { replace: true });
    }
  }, [navigate]);

  // Upfront gate check — no subscription + no card → warn first
  useEffect(() => {
    fetchDeliveryGate().then(({ canDeliver }) => setCanDeliver(canDeliver));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API}/api/bank/recipients?type=international`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setRecents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const pickRecent = (r) => {
    setRecipientName(r.name || "");
    setCountryName(r.countryName || "");
    setBankName(r.bankName || "");
    setAccountNumber(r.accountNumber || "");
    setSwiftCode(r.swiftCode || "");
  };

  // Balance check — ALWAYS evaluated before any gate warning
  const hasInsufficientFunds = () => Number(amount) > Number(user?.balance || 0);

  const insufficientMessage = () =>
    `This transfer of ${cur}${fmt(amount)} is more than your available balance of ${cur}${fmt(user?.balance)}. Please fund your wallet first.`;

  const validate = () => {
    if (!recipientName.trim()) return "Enter the recipient's full name.";
    if (!countryName.trim()) return "Enter the destination country.";
    if (!bankName.trim()) return "Enter the recipient's bank name.";
    if (!accountNumber.trim()) return "Enter the account number / IBAN.";
    if (!Number(amount) || Number(amount) <= 0) return "Enter a valid amount.";
    return null;
  };

  const handleSubmit = () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError("");

    // Balance check first — before the confirm screen even opens
    if (hasInsufficientFunds()) {
      setBlocker({
        icon: "💳",
        title: "Insufficient Balance",
        message: insufficientMessage(),
        action: { label: "Fund Wallet", path: "/purchase" },
      });
      return;
    }

    setStage("confirm");
  };

  // Called by PinPad with the entered 4-digit PIN
  const submitTransfer = async (pin) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${API}/api/bank/wallet/international`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          amount: Number(amount),
          recipient_name: recipientName.trim(),
          country_name: countryName.trim(),
          bank_name: bankName.trim(),
          account_number: accountNumber.trim(),
          swift_code: swiftCode.trim(),
          pin,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        // PIN errors are handled inside the PinPad itself
        if (["PIN_INCORRECT", "PIN_LOCKED", "PIN_NOT_SET", "PIN_REQUIRED"].includes(data.code)) {
          return { ok: false, data };
        }

        if (data.code === "ACCOUNT_LOCKED") {
          setBlocker({ icon: "🔒", title: "Account Locked", message: data.error });
        } else if (data.code === "ELITE_REQUIRED") {
          setBlocker({ icon: "👑", title: "Elite Plan Required", message: data.error, action: { label: "View Plans", path: "/subscription" } });
        } else if (data.code === "INSUFFICIENT_BALANCE") {
          setBlocker({ icon: "💳", title: "Insufficient Balance", message: data.error, action: { label: "Purchase Coins", path: "/purchase" } });
        } else {
          setError(data.error || "Transfer failed.");
        }
        setStage("form");
        return { ok: false, data };
      }

      const tx = data.transaction || {};
      tx.delivered = data.delivered !== false;
      setResult(tx);

      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        u.balance = tx.new_balance;
        localStorage.setItem("user", JSON.stringify(u));
        setUser(u);
      } catch {}

      setStage("processing");
      setTimeout(() => setStage("done"), 2400);
      return { ok: true, data };
    } catch {
      setError("Network error. Please try again.");
      setStage("form");
      return { ok: false, data: {} };
    }
  };

  // Confirm & Send → balance first, then gate, then PIN
  const handleConfirm = () => {
    // Balance check ALWAYS comes first — before the gate warning
    if (hasInsufficientFunds()) {
      setBlocker({
        icon: "💳",
        title: "Insufficient Balance",
        message: insufficientMessage(),
        action: { label: "Fund Wallet", path: "/purchase" },
      });
      return;
    }

    // Gate only when funds are sufficient
    if (!canDeliver) {
      setGateOpen(true);
      return;
    }

    setPinOpen(true);
  };

  const inputStyle = {
    width: "100%", padding: "13px 14px", borderRadius: 10,
    border: `1px solid ${theme.line}`, background: theme.inputBg,
    color: theme.text, fontSize: 14, outline: "none", boxSizing: "border-box",
  };

  const labelStyle = { display: "block", fontSize: 12, color: theme.sub, marginBottom: 6, fontWeight: 600 };

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, borderBottom: `1px solid ${theme.line}`, position: "sticky", top: 0, background: theme.bg, zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: theme.text, fontSize: 20, cursor: "pointer", padding: 4 }}>←</button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>International Transfer</div>
          <div style={{ fontSize: 11, color: theme.sub }}>Available balance: {cur}{fmt(user?.balance)}</div>
        </div>
        <div style={{ marginLeft: "auto", fontSize: 10, padding: "4px 10px", borderRadius: 20, background: theme.accentSoft, color: theme.accent, fontWeight: 700 }}>
          ELITE
        </div>
      </div>

      <div style={{ maxWidth: 460, margin: "0 auto", padding: "20px 20px 60px" }}>
        {stage === "form" && (
          <>
            {recents.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, color: theme.sub, fontWeight: 600, marginBottom: 10 }}>RECENT RECIPIENTS</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {recents.map((r, i) => (
                    <button key={i} onClick={() => pickRecent(r)}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", background: theme.card, border: `1px solid ${theme.line}`, borderRadius: 10, cursor: "pointer", textAlign: "left" }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: theme.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>🌍</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: theme.text, fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                        <div style={{ color: theme.sub, fontSize: 11 }}>{r.bankName} · {r.countryName}</div>
                      </div>
                      <span style={{ color: theme.sub, fontSize: 14 }}>›</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>RECIPIENT FULL NAME</label>
              <input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Full name as it appears on the account" style={inputStyle} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>DESTINATION COUNTRY</label>
              <input value={countryName} onChange={(e) => setCountryName(e.target.value)} placeholder="e.g. United Kingdom, United States, South Africa" style={inputStyle} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>BANK NAME</label>
              <input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. Barclays, Chase, Standard Bank" style={inputStyle} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>ACCOUNT NUMBER / IBAN</label>
              <input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="Account number or IBAN" style={inputStyle} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>SWIFT / BIC CODE <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
              <input value={swiftCode} onChange={(e) => setSwiftCode(e.target.value.toUpperCase())} placeholder="e.g. BARCGB22" style={inputStyle} />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>AMOUNT ({cur})</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0.00" inputMode="decimal" style={{ ...inputStyle, fontSize: 18, fontWeight: 700 }} />
            </div>

            {error && <div style={{ color: "#f87171", fontSize: 13, margin: "10px 0" }}>{error}</div>}

            <button onClick={handleSubmit}
              style={{ width: "100%", marginTop: 14, padding: "14px", borderRadius: 10, border: "none", background: theme.accent, color: theme.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
              Review Transfer
            </button>
          </>
        )}

        {stage === "confirm" && (
          <div style={{ background: theme.card, border: `1px solid ${theme.line}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "18px 20px", borderBottom: `1px solid ${theme.line}` }}>
              <div style={{ fontSize: 12, color: theme.sub, fontWeight: 600 }}>CONFIRM INTERNATIONAL TRANSFER</div>
            </div>
            <div style={{ padding: "20px" }}>
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <div style={{ fontSize: 11, color: theme.sub, marginBottom: 4 }}>You are sending</div>
                <div style={{ fontSize: 32, fontWeight: 800 }}>{cur}{fmt(amount)}</div>
              </div>
              {[
                ["Recipient", recipientName],
                ["Country", countryName],
                ["Bank", bankName],
                ["Account / IBAN", accountNumber],
                ...(swiftCode ? [["SWIFT / BIC", swiftCode]] : []),
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${theme.line}` }}>
                  <span style={{ color: theme.sub, fontSize: 13 }}>{k}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>{v}</span>
                </div>
              ))}
              <button onClick={handleConfirm}
                style={{ width: "100%", marginTop: 20, padding: "14px", borderRadius: 10, border: "none", background: theme.accent, color: theme.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
                Confirm &amp; Send
              </button>
              <button onClick={() => setStage("form")}
                style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 10, border: `1px solid ${theme.line}`, background: "none", color: theme.sub, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                Back
              </button>
            </div>
          </div>
        )}

        {stage === "processing" && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ width: 40, height: 40, margin: "0 auto 18px", border: `3px solid ${theme.line}`, borderTopColor: theme.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>Processing international transfer…</div>
            <div style={{ fontSize: 12, color: theme.sub, marginTop: 6 }}>Routing through the international payment network. Do not close this page.</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {stage === "done" && result && (
          <div style={{ background: theme.card, border: `1px solid ${theme.line}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "24px 20px", textAlign: "center", borderBottom: `1px solid ${theme.line}` }}>
              <div style={{
                width: 52, height: 52, margin: "0 auto 12px", borderRadius: "50%",
                background: result.delivered ? "#dcfce722" : "#f59e0b22",
                border: result.delivered ? "1px solid #22c55e44" : "1px solid #f59e0b44",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 24, color: result.delivered ? "#22c55e" : "#fbbf24",
              }}>
                {result.delivered ? "✓" : "⏳"}
              </div>
              <div style={{ fontWeight: 700, fontSize: 17 }}>
                {result.delivered ? "Transfer Submitted" : "Transfer Recorded"}
              </div>
              <div style={{ color: theme.sub, fontSize: 12, marginTop: 4 }}>
                {result.delivered
                  ? `International transfer to ${countryName}`
                  : "Held — pending subscription or card"}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, marginTop: 14 }}>{cur}{fmt(result.amount)}</div>
            </div>
            <div style={{ padding: "16px 20px" }}>
              {[
                ["Recipient", recipientName],
                ["Country", countryName],
                ["Bank", bankName],
                ["Account / IBAN", accountNumber],
                ...(swiftCode ? [["SWIFT / BIC", swiftCode]] : []),
                ["Transaction ID", result.transactionId || ""],
                ["New Balance", `${cur}${fmt(result.new_balance)}`],
                ["Status", result.delivered ? "Submitted" : "Pending — not yet with recipient"],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${theme.line}` }}>
                  <span style={{ color: theme.sub, fontSize: 12 }}>{k}</span>
                  <span style={{
                    fontSize: 12, fontWeight: 600, textAlign: "right", maxWidth: "60%", wordBreak: "break-all",
                    ...(k === "Status" && !result.delivered ? { color: "#fbbf24" } : {}),
                  }}>{v}</span>
                </div>
              ))}

              {!result.delivered && (
                <div style={{
                  marginTop: 14, padding: "12px 14px", borderRadius: 10,
                  background: "#f59e0b1a", border: "1px solid #f59e0b40",
                  color: "#fbbf24", fontSize: 12, lineHeight: 1.6,
                }}>
                  This transaction is recorded on your account, but it will not reach
                  the recipient until you subscribe to a plan or activate a ProBank card.
                </div>
              )}

              <button onClick={() => navigate("/dashboard")}
                style={{ width: "100%", marginTop: 18, padding: "14px", borderRadius: 10, border: "none", background: theme.accent, color: theme.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
                Back to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pre-transfer delivery warning (only when gated) */}
      <DeliveryGateModal
        open={gateOpen}
        brand={theme}
        onContinue={() => {
          setGateOpen(false);
          setPinOpen(true);
        }}
        onCancel={() => setGateOpen(false)}
      />

      {/* Transaction PIN pad */}
      <PinPad
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onSuccess={submitTransfer}
        title="Authorize International Transfer"
        subtitle={`${cur}${fmt(amount)} to ${recipientName || "recipient"}`}
        brand={{ accent: theme.accent, accentText: theme.accentText }}
      />

      {/* Blocker modal */}
      {blocker && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div style={{ background: theme.card, border: `1px solid ${theme.line}`, borderRadius: 14, padding: 26, maxWidth: 360, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 34, marginBottom: 12 }}>{blocker.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>{blocker.title}</div>
            <div style={{ color: theme.sub, fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>{blocker.message}</div>
            {blocker.action && (
              <button onClick={() => navigate(blocker.action.path)}
                style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: theme.accent, color: theme.accentText, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}>
                {blocker.action.label}
              </button>
            )}
            <button onClick={() => setBlocker(null)}
              style={{ width: "100%", padding: "11px", borderRadius: 10, border: `1px solid ${theme.line}`, background: "none", color: theme.sub, fontWeight: 600, cursor: "pointer" }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
